<template>
  <section class="py-9 tablet:py-20">
    <div class="container-wrap">
      <div
        class="mb-10 desktop:flex"
        :class="
          descriptionHtml || description
            ? 'desktop:items-end'
            : 'desktop:items-center'
        "
      >
        <div class="desktop:flex-1">
          <h2 class="text-h2 mb-10 last:mb-0 tablet:line-clamp-2">
            {{ title }}
          </h2>
          <TheRichText
            v-if="descriptionHtml || description"
            class="w-full text-sm leading-6 text-gray-700 tablet:line-clamp-7 tablet:text-base"
            :text="descriptionHtml || description"
            :is-html="!!descriptionHtml"
          />
        </div>
        <div
          v-if="tabs.length > 1"
          class="hidden w-40 basis-40 pl-5 desktop:block"
        >
          <div
            class="inline-flex h-16 items-center overflow-hidden rounded-[64px] bg-gray-600_a15"
          >
            <button
              class="flex h-16 w-16 items-center justify-center rounded-full text-black hover:bg-gray-600_a25 active:bg-gray-600_a35"
              @click="handlePrev()"
            >
              <IconCaret direction="left" />
            </button>

            <button
              class="flex h-16 w-16 items-center justify-center rounded-full text-black hover:bg-gray-600_a25 active:bg-gray-600_a35"
              @click="handleNext()"
            >
              <IconCaret direction="right" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <ClientOnly>
      <div v-if="tabs.length === 1" class="container-wrap">
        <PostcardQuote
          :title="tabs[0].name"
          :description="provideDescription(tabs[0])"
          :button-url="tabs[0].link"
          :button-id="tabs[0].trackingCode"
          :tag="tabs[0].tag"
          :image-url="tabs[0].thumbnail"
          :clickable="true"
          :has-shadow="isDesktop"
          :theme="theme"
        />
      </div>
      <div v-else>
        <div class="container-extend-wrap">
          <div class="mb-6">
            <swiper-container ref="mainContainerRef" :init="false">
              <swiper-slide
                v-for="item in tabs"
                :key="item.name"
                class="h-auto px-2.5 py-2.5 tablet:px-5 desktop:px-2.5"
              >
                <PostcardQuote
                  :title="item.name"
                  :description="provideDescription(item)"
                  :button-url="item.link"
                  :button-id="item.trackingCode"
                  :tag="item.tag"
                  :image-url="item.thumbnail"
                  :clickable="true"
                  :has-shadow="isDesktop"
                  :theme="theme"
                />
              </swiper-slide>
            </swiper-container>
          </div>
        </div>
        <div v-if="tabs.length > 1" class="container-wrap">
          <div
            class="flex justify-center border-t border-solid border-gray-500"
          >
            <swiper-container
              ref="thumbsContainerRef"
              :free-mode="true"
              :watch-slides-progress="true"
              slides-per-view="auto"
              class="inline-block overflow-hidden"
            >
              <swiper-slide
                v-for="(item, index) in tabs"
                :key="item.name"
                class="flex h-full w-[240px] shrink-0 justify-center desktop:w-[320px]"
              >
                <button
                  class="group relative w-[160px] py-3 text-center before:absolute before:left-0 before:top-0 before:block before:h-1.5 before:w-full before:rounded-b-lg before:opacity-0 before:content-['']"
                  :title="item.tabName"
                  :class="[tabAccentClass, current === index && tabActiveClass]"
                >
                  <figure class="aspect-[3/2]">
                    <img
                      :src="item.logo || ''"
                      :alt="item.tabName"
                      class="pointer-events-none block h-full w-full object-contain"
                      :class="
                        current !== index &&
                        'opacity-80 grayscale group-hover:opacity-100 group-hover:grayscale-0'
                      "
                    />
                  </figure>
                  <p class="truncate">{{ item.tabName }}</p>
                </button>
              </swiper-slide>
            </swiper-container>
          </div>
        </div>
      </div>
    </ClientOnly>

    <!-- For SEO -->
    <ul class="hidden">
      <li v-for="item in tabs" :key="item.name">
        <PostcardQuote
          :title="item.name"
          :description="provideDescription(item)"
          :button-url="item.link"
          :button-id="item.trackingCode"
          :tag="item.tag"
          :image-url="item.thumbnail"
          :theme="theme"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
  /**
   * @see https://www.figma.com/design/QJ3ZvEbUHI9xI11iudYf8U/Advantech-WISE-Library?node-id=12963-36609&t=89PIFDiQEC4zcSbf-0
   */

  import type { Swiper as SwiperInstance } from 'swiper/types';

  interface IItem {
    tabName: string;
    name: string;
    link: string;
    thumbnail: string;
    trackingCode?: string;
    tag?: string;
    logo?: string;
    authorName?: string;
    authorJobTitle?: string;
    authorCompany?: string;
  }

  interface IProps {
    title: string;
    description?: string;
    descriptionHtml?: string;
    tabs: IItem[];
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    description: undefined,
    descriptionHtml: undefined,
    theme: 'blue',
  });

  const { isDesktop } = useScreen();

  const current = ref<number>(0);

  const mainContainerRef = ref<null>(null);
  const thumbsContainerRef = ref<null>(null);
  const mainSwiper = shallowRef<SwiperInstance | null>(null);

  const thumbsSwiper = useSwiper(thumbsContainerRef);

  const provideDescription = (item: IItem): string => {
    return [item.authorName, item.authorJobTitle, item.authorCompany]
      .filter(Boolean)
      .join(', ');
  };

  const tabAccentClass = computed((): string => {
    switch (props.theme) {
      case 'green':
        return 'before:bg-green-400 hover:text-green-400';
      case 'cyan':
        return 'before:bg-cyan-400 hover:text-cyan-400';
      case 'purple':
        return 'before:bg-purple-400 hover:text-purple-400';
      case 'yellow':
        return 'before:bg-yellow-400 hover:text-yellow-400';
      default:
        return 'before:bg-blue-400 hover:text-blue-400';
    }
  });

  const tabActiveClass = computed((): string => {
    switch (props.theme) {
      case 'green':
        return 'text-green-400 before:opacity-100';
      case 'cyan':
        return 'text-cyan-400 before:opacity-100';
      case 'purple':
        return 'text-purple-400 before:opacity-100';
      case 'yellow':
        return 'text-yellow-400 before:opacity-100';
      default:
        return 'text-blue-400 before:opacity-100';
    }
  });

  const handlePrev = (): void => {
    mainSwiper.value?.slidePrev();
  };

  const handleNext = (): void => {
    mainSwiper.value?.slideNext();
  };

  const onMainSlideChange = (event: CustomEvent): void => {
    const swiperInstance = event.detail[0];
    current.value = swiperInstance.realIndex;
  };

  onMounted(() => {
    nextTick(() => {
      // Thumbs 需要主 swiper 連結到一個已初始化的縮圖 swiper 實例，故延後主 swiper 的初始化
      const stopWatch = watch(
        thumbsSwiper.instance,
        (thumbsInstance) => {
          const mainEl = mainContainerRef.value as
            | (HTMLElement & {
                swiper?: SwiperInstance;
                initialize: () => void;
              })
            | null;
          if (!thumbsInstance || !mainEl) return;

          Object.assign(mainEl, {
            loop: true,
            thumbs: { swiper: thumbsInstance },
          });
          mainEl.addEventListener(
            'swiperslidechange',
            onMainSlideChange as EventListener
          );
          mainEl.initialize();
          mainSwiper.value = mainEl.swiper ?? null;
          stopWatch();
        },
        { immediate: true }
      );
    });
  });

  export type { IItem };
</script>
